import { Component, inject, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DocumentDialogComponent } from '@components/document-dialog';
import {
	BusinessMatterClient,
	DefaultMatterAffidavit,
	Document,
	MatterAffidavit,
	PersonMatterClient,
} from '@models/matter';
import { GroupType } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { AffidavitDispatch, AffidavitState } from '@state/matter/affidavit';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { combineLatest, Observable, Subject, timer } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { DropdownResult } from '../../../shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { CustomStatementComponent } from './custom-statement/custom-statement.component';
import { ImportDetailsComponent } from './import-details/import-details.component';
import { MergedMatterDocumentSelectors } from '@state/global/documents/merged-documents';
import { getFolderHashedId } from '@core/util/documents';
import { SaleWritState } from '@state/sale_writ';
import { clientLegalName } from '@core/util/client';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { TestHooks } from './execution-affidavits.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

interface StatementOption {
	action: 'create' | 'import';
	icon: ICON | '';
	name: string;
}

@UntilDestroy()
@Component({
	selector: 'app-execution-affidavits',
	templateUrl: 'execution-affidavits.component.html',
	styleUrls: ['execution-affidavits.component.scss'],
	standalone: false,
})
export class ExecutionAffidavitsComponent implements OnDestroy {
	testHooks = TestHooks;
	drawerService = inject(DrawerService);

	@Select(AffidavitState.getAll) public affidavits$: Observable<
		MatterAffidavit[]
	>;
	@Select(MatterClients.get_clients) public clients$: Observable<
		(PersonMatterClient | BusinessMatterClient)[]
	>;
	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;

	@Select(SaleWritState.hasExecutions)
	public hasExecutions$: Observable<boolean>;
	@ViewSelectSnapshot(MatterClients.get_clients) public clients: (
		| PersonMatterClient
		| BusinessMatterClient
	)[];

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	public documents: { [key: string]: Document[] };
	@Select(MergedMatterDocumentSelectors.getDocumentsByFolderId)
	public documentsGroupFilterFn$: Observable<(id: string) => Document[]>;

	public dropdownVisible: { [key: string]: boolean };
	public ICON = ICON;
	public readonly StatementOptions: StatementOption[];

	private hideDropdown$ = new Subject<
		PersonMatterClient | BusinessMatterClient
	>();
	private showDropdown$ = new Subject<
		PersonMatterClient | BusinessMatterClient
	>();

	constructor(
		private affidavitDispatch: AffidavitDispatch,
		private dialog: MatDialog,
		private navigationService: NavigationService,
	) {
		this.documents = {};
		this.dropdownVisible = {};

		combineLatest([this.clients$, this.documentsGroupFilterFn$])
			.pipe(untilDestroyed(this))
			.subscribe(([clients, filterFn]) => {
				clients.forEach((client) => {
					this.documents[client.id] = filterFn(
						getFolderHashedId(
							this.matterId,
							GroupType.AFFIDAVIT,
							client.id,
							true,
						),
					);
				});
			});
		this.hideDropdown$
			.pipe(
				untilDestroyed(this),
				debounce((ev) => (ev ? timer(0) : timer(500))),
			)
			.subscribe((client) => {
				this.dropdownVisible[client.id] = false;
			});
		this.showDropdown$
			.pipe(
				untilDestroyed(this),
				debounce((ev) => (ev ? timer(0) : timer(500))),
			)
			.subscribe((client) => {
				this.dropdownVisible[client.id] = true;
			});
		this.StatementOptions = [
			{
				action: 'create',
				icon: '',
				name: 'Create custom statement',
			},
			{
				action: 'import',
				icon: '',
				name: 'Import Teranet search results',
			},
		];
	}

	ngOnDestroy(): void {
		this.hideDropdown$.complete();
		this.showDropdown$.complete();
	}

	addImport(clientId: string, onlyShowExecutions = false): void {
		this.dialog.open(ImportDetailsComponent, {
			autoFocus: true,
			data: {
				clientId,
				onlyShowExecutions,
			},
			maxHeight: '100%',
			minWidth: '900px',
			width: '90%',
		});
	}

	addStatement(clientId: string): void {
		this.dialog.open(CustomStatementComponent, {
			autoFocus: true,
			data: {
				affidavit: {
					...DefaultMatterAffidavit,
					clientId,
				},
			},
			maxHeight: '100%',
			maxWidth: '500px',
			width: '90%',
		});
	}

	affidavitId(index: number, affidavit: MatterAffidavit): string {
		return affidavit.id;
	}

	clientId(
		index: number,
		client: PersonMatterClient | BusinessMatterClient,
	): string {
		return client.id;
	}

	deleteStatement(affidavitId: string): void {
		this.affidavitDispatch.Remove(affidavitId);
	}

	documentId(index: number, document: Document): string {
		return document.id;
	}

	editStatement(clientId: string, affidavit: MatterAffidavit): void {
		this.dialog.open(CustomStatementComponent, {
			autoFocus: true,
			data: {
				affidavit,
			},
			maxHeight: '100%',
			maxWidth: '500px',
			width: '90%',
		});
	}

	hideDropdown(client: PersonMatterClient | BusinessMatterClient): void {
		this.hideDropdown$.next(client);
	}

	onSelect(
		event: DropdownResult<StatementOption>,
		client: PersonMatterClient | BusinessMatterClient,
	): void {
		this.dropdownVisible[client.id] = false;
		this.hideDropdown(client);

		switch (event.result.action) {
			case 'create':
				this.addStatement(client.id);
				break;
			case 'import':
				this.addImport(client.id);
				break;
		}
	}

	openDocument(
		document: Document,
		client: PersonMatterClient | BusinessMatterClient,
	): void {
		this.drawerService.openDrawer(DocumentDialogComponent, {
			title: `${document.title} ${clientLegalName(client, this.nameFormattingSettings)}`,
			data: {
				documentId: document.id,
				title: [
					document.title,
					clientLegalName(client, this.nameFormattingSettings),
				]
					.filter((segment) => !!segment)
					.join(': '),
			},
			noStyle: true,
		});
	}

	showDropdown(client: PersonMatterClient | BusinessMatterClient): void {
		this.showDropdown$.next(client);
	}

	goToParties() {
		this.navigationService.goToMatter_Parties(this.matterId);
	}
}
